import { redirect } from "@tanstack/react-router";
import { IS_AUTH_GUARD_ACTIVE, DEFAULT_PATHS } from "../config";
import useAuthStore from "../stores/authStore";

export const userHasRole = (routeRoles = [], userRole = []) => {
  if (!routeRoles || routeRoles.length === 0) return true;
  return routeRoles.some((r) => userRole.includes(r));
};

export const authGuard = ({ roles = [], publicOnly = false } = {}) => {
  return () => {
    if (!IS_AUTH_GUARD_ACTIVE) {
      return;
    }

    const { isLogin, currentUser } = useAuthStore.getState();
    const userPermissions = currentUser?.role?.permissions || [];

    if (publicOnly) {
      if (isLogin) {
        throw redirect({
          to: DEFAULT_PATHS.USER_WELCOME,
        });
      }
      return;
    }

    if (roles && roles.length > 0) {
      if (!isLogin) {
        throw redirect({
          to: DEFAULT_PATHS.LOGIN,
          search: { from: window.location.pathname },
        });
      }

      if (!userHasRole(roles, userPermissions)) {
        throw redirect({
          to: DEFAULT_PATHS.UNAUTHORIZED,
          search: { from: window.location.pathname },
        });
      }
    }
  };
};

export const permissionCheck = (permissions = []) => {
  const { currentUser } = useAuthStore.getState();
  const userPermissions = currentUser?.role?.permissions || [];
  const _permissions = Array.isArray(permissions) ? permissions : [permissions];

  return userHasRole(_permissions, userPermissions);
};

export const requireAuth = () => {
  return () => {
    if (!IS_AUTH_GUARD_ACTIVE) {
      return;
    }

    const { isLogin } = useAuthStore.getState();

    if (!isLogin) {
      throw redirect({
        to: DEFAULT_PATHS.LOGIN,
        search: { from: window.location.pathname },
      });
    }
  };
};

const clearMenuItem = (menuItem) => {
  const item = {};
  ["path", "label", "icon", "isExternal", "subs", "mega", "megaParent"].forEach(
    (key) => {
      if (menuItem[key]) item[key] = menuItem[key];
    }
  );
  return item;
};

export const convertToMenuItems = ({
  data = [],
  authGuardActive = IS_AUTH_GUARD_ACTIVE,
  isLogin = false,
  userRole = null,
}) => {
  let items = [];
  if (Array.isArray(data)) {
    items = data;
  } else {
    items = [...data.sidebarItems, ...data.mainMenuItems];
  }

  const itemMapper = (item) => {
    const tempItem = { ...item };

    if (authGuardActive) {
      if (tempItem.roles) tempItem.protected = true;

      if (tempItem.publicOnly) {
        delete tempItem.roles;
        delete tempItem.protected;
      }

      if (tempItem.protected) {
        if (!isLogin) {
          return {};
        }
        if (tempItem.roles) {
          if (!userHasRole(tempItem.roles, userRole)) {
            return {};
          }
        }
      } else if (tempItem.publicOnly && isLogin) {
        return {};
      }
    }

    if (tempItem.subs) {
      tempItem.subs = item.subs
        .map((sub) => {
          const controlledSub = { ...sub, path: tempItem.path + sub.path };
          if (tempItem.mega || tempItem.megaParent)
            controlledSub.megaParent = true;

          if (authGuardActive) {
            if (tempItem.protected) controlledSub.protected = true;

            if (tempItem.roles) {
              if (!sub.roles) controlledSub.roles = tempItem.roles;
              else {
                // common roles..
                controlledSub.roles = tempItem.roles.filter((x) =>
                  sub.roles.includes(x)
                );

                if (controlledSub.roles && controlledSub.roles.length === 0) {
                  controlledSub.inaccessible = true;
                }
              }
            } else if (tempItem.publicOnly) {
              controlledSub.publicOnly = true;
            }
            if (controlledSub.roles && controlledSub.roles.length === 0)
              delete controlledSub.roles;

            if (!controlledSub.inaccessible) return itemMapper(controlledSub);
            return itemMapper({});
          }
          return itemMapper(controlledSub);
        })
        .filter((x) => Object.keys(x).length > 0);

      if (tempItem.subs.length === 0) delete tempItem.subs;
    }
    if (tempItem.label && !item.hideInMenu) return clearMenuItem(tempItem);
    return {};
  };
  return items.map(itemMapper).filter((x) => Object.keys(x).length > 0);
};

export const getMenuItems = ({ data, isLogin, userRole }) =>
  convertToMenuItems({
    data,
    isLogin,
    userRole,
    authGuardActive: IS_AUTH_GUARD_ACTIVE,
  });
